import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ratesService,
  type CreateRateData,
  type UpdateRateData,
  type RatesQueryParams,
} from '@/services/rates.service';

// Query key factory
export const rateKeys = {
  all: ['rates'] as const,
  lists: () => [...rateKeys.all, 'list'] as const,
  list: (params?: RatesQueryParams) => [...rateKeys.lists(), params] as const,
  details: () => [...rateKeys.all, 'detail'] as const,
  detail: (id: string) => [...rateKeys.details(), id] as const,
};

// Get all rates
export function useRates(params?: RatesQueryParams) {
  return useQuery({
    queryKey: rateKeys.list(params),
    queryFn: () => ratesService.getRates(params),
  });
}

// Get single rate
export function useRate(id: string) {
  return useQuery({
    queryKey: rateKeys.detail(id),
    queryFn: () => ratesService.getRate(id),
    enabled: !!id,
  });
}

// Create rate
export function useCreateRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRateData) => ratesService.createRate(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rateKeys.lists() });
    },
  });
}

// Update rate
export function useUpdateRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRateData }) =>
      ratesService.updateRate(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: rateKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: rateKeys.detail(variables.id) });
    },
  });
}

// Delete rate
export function useDeleteRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ratesService.deleteRate(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rateKeys.lists() });
    },
  });
}
